/* eslint-disable @typescript-eslint/no-explicit-any */
import { GridActionsCellItem } from '@mui/x-data-grid/components';
import { GridColDef,  } from '@mui/x-data-grid/models/colDef/gridColDef';
import { Delete, Edit, Group } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { Subscription } from '@/api/bol-notifications';
import { renderChip } from '@/utils/datagridFormatters';

interface SubscriptionColumnsProps {
  onViewConsumers: (subscriptionId: string) => void;
  onEdit: (subscription: Subscription) => void;
  onDelete: (subscription: Subscription) => void;
  canEdit?: boolean;
  canDelete?: boolean;
  editTooltip?: string;
  deleteTooltip?: string;
}

export const createSubscriptionColumns = ({ onViewConsumers, onEdit, onDelete, canEdit = true, canDelete = true, editTooltip = '', deleteTooltip = '' }: SubscriptionColumnsProps): GridColDef[] => [
  {
    field: 'id',
    headerName: 'ID',
    flex: 1,
    minWidth: 200,
  },
  {
    field: 'name',
    headerName: 'Name',
    flex: 1,
    minWidth: 200,
  },
  {
    field: 'memberIdentifier',
    headerName: 'Member',
    flex: 1,
    minWidth: 150,
  },
  {
    field: 'notificationType',
    headerName: 'Type',
    width: 100,
    renderCell: (params: any) => renderChip('notificationType', params.value),
  },
  {
    field: 'resourceType',
    headerName: 'Resource',
    width: 120,
    renderCell: (params: any) => renderChip('resourceType', params.value, 'outlined'),
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 100,
    renderCell: (params: any) => renderChip('subscriptionStatus', params.value),
  },
  {
    field: 'disabledAt',
    headerName: 'Disabled At',
    width: 150,
    valueFormatter: (value: string | number | Date | null | undefined) => value ? new Date(value).toLocaleString() : '-',
  },
  {
    field: 'createdAt',
    headerName: 'Created',
    width: 150,
    valueFormatter: (value: string | number | Date) => new Date(value).toLocaleDateString(),
  },
  {
    field: 'updatedAt',
    headerName: 'Updated',
    width: 150,
    valueFormatter: (value: string | number | Date) => new Date(value).toLocaleDateString(),
  },
  {
    field: 'cdcEnabled',
    headerName: 'CDC Enabled',
    width: 120,
    valueGetter: (value: any, row: Subscription) => row.cdc?.enabled ? 'Yes' : 'No',
  },
  {
    field: 'cdcDebeziumTopic',
    headerName: 'CDC Debezium Topic',
    flex: 1,
    minWidth: 200,
    valueGetter: (value: any, row: Subscription) => row.cdc?.debeziumKafkaTopic || '-',
  },
  {
    field: 'cdcNotificationsTopic',
    headerName: 'CDC Notifications Topic',
    flex: 1,
    minWidth: 200,
    valueGetter: (value: any, row: Subscription) => row.cdc?.notificationsKafkaTopic || '-',
  },
  {
    field: 'cdcCorrelationId',
    headerName: 'CDC Correlation ID',
    flex: 1,
    minWidth: 200,
    valueGetter: (value: any, row: Subscription) => row.cdc?.correlationId || '-',
  },
  {
    field: 'actions',
    type: 'actions',
    headerName: 'Actions',
    width: 180,
    hideable: false, // Always visible
    getActions: (params: { row: Subscription; }) => [
      <GridActionsCellItem
        key="consumers"
        icon={<Group />}
        label="View Consumers"
        onClick={() => onViewConsumers(params.row.id)}
        color="primary"
      />,
      <Tooltip title={!canEdit ? editTooltip : ''} key="edit-tooltip">
        <span>
          <GridActionsCellItem
            icon={<Edit />}
            label="Edit"
            onClick={() => onEdit(params.row)}
            disabled={!canEdit}
            color="primary"
          />
        </span>
      </Tooltip>,
      <Tooltip title={!canDelete ? deleteTooltip : ''} key="delete-tooltip">
        <span>
          <GridActionsCellItem
            icon={<Delete />}
            label="Delete"
            onClick={() => onDelete(params.row)}
            disabled={!canDelete}
            color="error"
          />
        </span>
      </Tooltip>,
    ],
  },
];

export const defaultSubscriptionColumnVisibilityModel = {
  // Default visible columns
  name: true,
  memberIdentifier: true,
  notificationType: true,
  resourceType: true,
  status: true,
  createdAt: true,
  actions: true,
  // Hidden by default
  id: false,
  disabledAt: false,
  updatedAt: false,
  cdcEnabled: false,
  cdcDebeziumTopic: false,
  cdcNotificationsTopic: false,
  cdcCorrelationId: false,
};